// Orthomosaic reference + shape/measure helpers.
// Real tile pyramids are gdal2tiles *raster* profile (pure pixel space, no
// CRS — facades have none), so the viewer runs on L.CRS.Simple: a map point
// is the ortho pixel grid scaled by 2^maxZoom (Leaflet's unproject
// convention), with lat negated (screen y grows downward).
// Defect positions are stored as % of the ortho footprint (x: left→right,
// y: top→bottom) and mapped here against the ACTIVE ortho's dimensions.
import L from "leaflet";
import type { DefectShape } from "./types";

export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** WGS84 footprint of a mercator-tiled ortho: envelope for the tile layer +
 * (optionally) the corner quad from gdalinfo (UL, LL, LR, UR) so percent
 * coords correct for UTM grid convergence instead of assuming a
 * north-aligned rectangle. */
export interface OrthoBounds3857 {
  env: [number, number, number, number]; // [w, s, e, n]
  quad?: [number, number][]; // [lng, lat] × 4: ul, ll, lr, ur
}

export interface ActiveOrtho {
  id: string;
  tileUrl: string; // .../{z}/{x}/{y}.png template (raster profile)
  width: number; // px at native zoom
  height: number;
  maxZoom: number;
  tileSize: number;
  /** metres per native pixel (from the pipeline's defects.json header); null = measurements unavailable */
  gsdM: number | null;
  /** Mercator pyramid (projected orthos only): when present the viewer runs
   * a real EPSG:3857 map with the dark street basemap under the ortho. */
  bounds3857?: OrthoBounds3857 | null;
  tileUrl3857?: string | null;
  minZoom3857?: number | null;
  maxZoom3857?: number | null;
  /** Ad hoc photo surface: render this image on a flat CRS.Simple pixel plane
   * (no tile pyramid, no georeference, no measurement). Set width/height to the
   * image's native pixels, maxZoom 0, gsdM null. */
  photoSrc?: string | null;
}

/** True when the active surface is an ad hoc photo (flat image, no scale). */
export const isPhotoSurface = (o: ActiveOrtho | null = ACTIVE): boolean => !!(o && o.photoSrc);

/** True when the active ortho renders on the geographic (EPSG:3857) path. */
export const isGeographic = (o: ActiveOrtho | null = ACTIVE): boolean =>
  !!(o && o.bounds3857 && o.tileUrl3857);

// Module-level active ortho: the workspace sets it before the viewer mounts
// (the viewer remounts per ortho via a React key). Shared by the viewer,
// AreaTag and measure tools without threading a prop through every consumer.
let ACTIVE: ActiveOrtho | null = null;
export const setActiveOrtho = (o: ActiveOrtho | null) => { ACTIVE = o; AFFINE = null; };
export const activeOrtho = (): ActiveOrtho | null => ACTIVE;

const dims = () => {
  const o = ACTIVE;
  if (!o) return { w: 100, h: 100, s: 1 };
  return { w: o.width, h: o.height, s: Math.pow(2, o.maxZoom) };
};

// ── Geographic percent↔latlng: affine fit to the WGS84 corner quad ────
// (u,v) in [0,1] over the ortho grid → lnglat = UL + u·(UR−UL) + v·(LL−UL).
// A rotated rectangle (UTM grid convergence, ~1° here ≈ 7 m at the far
// corners) is represented exactly; residual warp inside this extent is
// sub-pixel. Falls back to the envelope when no quad was recorded (a plain
// north-aligned linear mapping — the documented approximation).
interface Affine { ox: number; oy: number; ax: number; ay: number; bx: number; by: number }
let AFFINE: Affine | null = null;
const affine = (): Affine | null => {
  if (AFFINE) return AFFINE;
  const b = ACTIVE?.bounds3857;
  if (!b) return null;
  const [w, s, e, n] = b.env;
  const q = b.quad && b.quad.length === 4 ? b.quad : null;
  const ul = q ? q[0]! : [w, n];
  const ur = q ? q[3]! : [e, n];
  const ll = q ? q[1]! : [w, s];
  AFFINE = { ox: ul[0]!, oy: ul[1]!, ax: ur[0]! - ul[0]!, ay: ur[1]! - ul[1]!, bx: ll[0]! - ul[0]!, by: ll[1]! - ul[1]! };
  return AFFINE;
};

/** Leaflet bounds of the active ortho (geographic envelope, or CRS.Simple
 * pixel space scaled by 2^maxZoom). */
export function orthoBounds(): L.LatLngBounds {
  const b = ACTIVE?.bounds3857;
  if (b) {
    const [w, s, e, n] = b.env;
    return L.latLngBounds(L.latLng(s, w), L.latLng(n, e));
  }
  const { w, h, s } = dims();
  return L.latLngBounds(L.latLng(-h / s, 0), L.latLng(0, w / s));
}

export const pctToLatLng = (d: { x: number; y: number }): L.LatLng => {
  const A = affine();
  if (A) {
    const u = d.x / 100, v = d.y / 100;
    return L.latLng(A.oy + u * A.ay + v * A.by, A.ox + u * A.ax + v * A.bx);
  }
  const { w, h, s } = dims();
  return L.latLng(-((d.y / 100) * h) / s, ((d.x / 100) * w) / s);
};

export const latLngToPct = (ll: L.LatLng): [number, number] => {
  const A = affine();
  if (A) {
    const det = A.ax * A.by - A.bx * A.ay;
    if (det !== 0) {
      const dx = ll.lng - A.ox, dy = ll.lat - A.oy;
      const u = (dx * A.by - A.bx * dy) / det;
      const v = (A.ax * dy - dx * A.ay) / det;
      return [clamp(u * 100, 0, 100), clamp(v * 100, 0, 100)];
    }
  }
  const { w, h, s } = dims();
  return [clamp(((ll.lng * s) / w) * 100, 0, 100), clamp(((-ll.lat * s) / h) * 100, 0, 100)];
};

// ── Defect shapes ─────────────────────────────────────────────────────
export const rectCorners = (pts: [number, number][]): [number, number][] => {
  const [p1, p2] = pts;
  const [x1, y1] = p1 ?? [0, 0];
  const [x2, y2] = p2 ?? [0, 0];
  return [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
};
export const shapePts = (shape: DefectShape): [number, number][] =>
  shape.type === "rect" ? rectCorners(shape.points) : shape.points;
export const shapeCentroid = (shape: DefectShape): [number, number] => {
  const pts = shapePts(shape);
  return [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];
};
export const translateShape = (shape: DefectShape, dx: number, dy: number): DefectShape => ({
  ...shape,
  points: shape.points.map(([x, y]) => [clamp(x + dx, 0, 100), clamp(y + dy, 0, 100)] as [number, number]),
});

// ── Measurement helpers ───────────────────────────────────────────────
// Geographic mode: real geodesic maths on the WGS84 sphere (design parity).
// CRS.Simple: one latlng unit = 2^maxZoom native pixels = 2^maxZoom × gsd
// metres; no GSD → measurements unavailable (null / "—").
const metresPerUnit = (): number | null => {
  const o = ACTIVE;
  if (!o || o.gsdM == null) return null;
  return Math.pow(2, o.maxZoom) * o.gsdM;
};

export function geoDist(a: L.LatLng, b: L.LatLng): number | null {
  if (isGeographic()) return a.distanceTo(b); // haversine (L.CRS.Earth)
  const m = metresPerUnit();
  if (m == null) return null;
  return Math.hypot(a.lng - b.lng, a.lat - b.lat) * m;
}

/** Polygon area in m²: spherical excess in geographic mode (accurate to well
 * under 1% at building scale), GSD-scaled shoelace in pixel space, or null
 * when neither is available. */
export function geoArea(latlngs: L.LatLng[]): number | null {
  if (isGeographic()) {
    const R = 6378137, rad = Math.PI / 180;
    let a = 0;
    for (let i = 0, n = latlngs.length; i < n; i++) {
      const p = latlngs[i]!, q = latlngs[(i + 1) % n]!;
      a += (q.lng - p.lng) * rad * (2 + Math.sin(p.lat * rad) + Math.sin(q.lat * rad));
    }
    return Math.abs((a * R * R) / 2);
  }
  const m = metresPerUnit();
  if (m == null) return null;
  let a = 0;
  for (let i = 0, n = latlngs.length; i < n; i++) {
    const p = latlngs[i]!, q = latlngs[(i + 1) % n]!;
    a += p.lng * q.lat - q.lng * p.lat;
  }
  return Math.abs(a / 2) * m * m;
}

export const fmtDist = (m: number | null) =>
  m == null ? "—" : m >= 1000 ? (m / 1000).toFixed(2) + "km" : m >= 100 ? Math.round(m) + "m" : m.toFixed(1) + "m";
export const fmtArea = (m2: number | null) =>
  m2 == null ? "—" : m2 >= 10000 ? (m2 / 10000).toFixed(2) + "ha" : m2 >= 100 ? Math.round(m2) + "m²" : m2.toFixed(1) + "m²";

// Real-world area of an outlined defect, from its pixel footprint.
export const shapeAreaM2 = (shape: DefectShape | undefined): number | null =>
  shape ? geoArea(shapePts(shape).map(([x, y]) => pctToLatLng({ x, y }))) : null;
export const defectAreaM2 = (d: { shape?: DefectShape } | null | undefined): number | null =>
  d && d.shape ? shapeAreaM2(d.shape) : null;

export const MEASURE_TIP =
  "Measured flat, straight down from above. Sloped surfaces, like a pitched roof, are larger in reality.";
